import type { FC, ReactNode } from 'react'
import type { Service } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, Check, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { WhatsAppButton, buttonClass } from '@/components/generals/cta-buttons'
import { essentials } from '@/constants/content'
import { services } from '@/constants/services'

interface ServiceRowProps {
    service: Service
    index: number
}

const ServiceRow: FC<ServiceRowProps> = ({ service, index }): ReactNode => {
    const isReversed = index % 2 === 1
    const number = String(index + 1).padStart(2, '0')
    const titleId = `service-${service.slug}-title`

    return (
        <article
            id={`service-${service.slug}`}
            aria-labelledby={titleId}
            className='grid scroll-mt-28 items-center gap-10 lg:grid-cols-2 lg:gap-16'
        >
            <div className={cn('relative mx-auto w-full max-w-xl lg:max-w-none', isReversed && 'lg:order-2')}>
                <div
                    aria-hidden='true'
                    className={cn(
                        'absolute -bottom-4 size-full rounded-[2rem] border border-gold/40',
                        isReversed ? '-left-4' : '-right-4'
                    )}
                />

                <div className='relative aspect-[4/3] overflow-hidden rounded-[2rem] bg-muted shadow-xl shadow-plum/10 sm:aspect-[16/11] lg:aspect-[5/6]'>
                    <Image
                        src={service.image}
                        alt={service.imageAlt}
                        fill
                        sizes='(min-width: 1024px) 45vw, (min-width: 640px) 36rem, 100vw'
                        className={cn('object-cover transition-transform duration-700 hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100', service.imageClassName)}
                    />
                </div>

                <span
                    aria-hidden='true'
                    className='gold-gradient absolute -top-5 left-6 flex size-14 items-center justify-center rounded-full font-heading text-lg font-bold text-plum-deep shadow-lg ring-4 ring-white'
                >
                    {number}
                </span>
            </div>

            <div>
                <p className='text-xs font-semibold uppercase tracking-[0.26em] text-gold-dark'>
                    {service.title}
                </p>

                <h3 id={titleId} className='mt-3 font-heading text-2xl font-semibold text-plum sm:text-3xl lg:text-[2.1rem]'>
                    {service.name}
                </h3>

                <p className='mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg'>
                    {service.short}
                </p>

                <ul className='mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-2'>
                    {service.highlights.map(highlight => (
                        <li key={highlight} className='flex items-start gap-2.5 text-sm leading-relaxed text-foreground/85'>
                            <Check className='mt-0.5 size-4 shrink-0 text-gold-dark' aria-hidden='true' />
                            {highlight}
                        </li>
                    ))}
                </ul>

                <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                    <WhatsAppButton
                        message={service.whatsappMessage}
                        label='Enquire on WhatsApp'
                        variant='plum'
                    />

                    <Link href={`/services/${service.slug}`} className={buttonClass('outline-dark')}>
                        View details
                        <span className='sr-only'> about {service.name}</span>
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </div>
            </div>
        </article>
    )
}

interface ServicesSectionProps {
    headingAs?: 'h1' | 'h2'
}

export const ServicesSection: FC<ServicesSectionProps> = ({ headingAs = 'h2' }): ReactNode => {
    return (
        <section
            id='services'
            aria-labelledby='services-title'
            className='blush-glow relative bg-white py-20 sm:py-24 lg:py-28'
        >
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='services-title'
                    as={headingAs}
                    eyebrow='Our specialities'
                    title='All kinds of beauty facilities are available'
                    description='From your wedding day to your everyday glow, here is everything we do for you, with care and only high quality products.'
                />

                <nav aria-label='Jump to a service' className='mt-10'>
                    <ul className='flex flex-wrap justify-center gap-2'>
                        {services.map(service => (
                            <li key={service.slug}>
                                <a
                                    href={`#service-${service.slug}`}
                                    className='inline-flex min-h-10 items-center rounded-full border border-gold/40 bg-white px-4 py-2 text-sm font-medium text-plum shadow-sm transition-colors hover:border-gold hover:bg-accent/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                                >
                                    {service.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='mt-16 space-y-24 lg:mt-20 lg:space-y-28'>
                    {services.map((service, index) => (
                        <ServiceRow key={service.slug} service={service} index={index} />
                    ))}
                </div>

                <div className='mt-24 rounded-3xl border border-gold/30 bg-ivory px-6 py-8 text-center shadow-sm sm:px-10'>
                    <p className='inline-flex items-center gap-2 font-heading text-lg font-semibold text-plum'>
                        <Sparkles className='size-5 text-gold-dark' aria-hidden='true' />
                        Everyday beauty care, too
                    </p>

                    <ul className='mt-5 flex flex-wrap justify-center gap-2'>
                        {essentials.map(item => (
                            <li key={item} className='rounded-full border border-border bg-white px-4 py-1.5 text-sm text-plum'>
                                {item}
                            </li>
                        ))}
                    </ul>

                    <p className='mt-5 text-sm text-muted-foreground'>
                        Looking for something else? Ask us on WhatsApp and we will be happy to help.
                    </p>
                </div>
            </div>
        </section>
    )
}
